import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { listCampaignTypes } from '../api/campaigns';
import type { CampaignTypeRow } from '../lib/db-types';

/**
 * Maestra de tipos de campaña (U6): impulso, captadores, mercaderismo, mystery shopper,
 * auditorías, ventas, BTL y los que vengan.
 *
 * La tabla existe desde la 0013 y `campaigns.type` es una clave foránea contra ella, pero **nunca
 * tuvo pantalla**: agregar un tipo exigía SQL. Y hasta el 15-ago el formulario de campaña tenía la
 * lista cableada a tres valores, lo que rompía en silencio el guardado de cualquier campaña con
 * otro tipo (§3.22). Tener la maestra sin forma de editarla fue lo que dejó pasar ese error tanto
 * tiempo: nadie la miraba.
 *
 * **El código no se edita después de creado.** Es la clave foránea que usan las campañas: cambiarlo
 * obligaría a actualizar en cascada y dejaría campañas apuntando a un tipo que ya no existe. El
 * nombre sí, porque es solo lo que se lee en pantalla.
 *
 * **Los tipos no se borran, se desactivan.** Un tipo con campañas históricas colgando no se puede
 * borrar (lo impide la FK) y tampoco conviene: el histórico dejaría de saber qué era esa campaña.
 */
export default function MaestraTiposCampania() {
  const [tipos, setTipos] = useState<CampaignTypeRow[]>([]);
  const [todos, setTodos] = useState<CampaignTypeRow[]>([]);
  const [verInactivos, setVerInactivos] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const [nuevoCodigo, setNuevoCodigo] = useState('');
  const [nuevoNombre, setNuevoNombre] = useState('');

  const cargar = useCallback(async () => {
    setError(null);
    try {
      // `listCampaignTypes` filtra los activos; para administrarlos hace falta ver también los
      // desactivados, o no habría forma de volver a encenderlos.
      const { data, error: e } = await supabase
        .from('campaign_types').select('*').order('sort').order('name');
      if (e) throw new Error(e.message);
      setTodos((data ?? []) as CampaignTypeRow[]);
      setTipos(await listCampaignTypes());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar los tipos.');
    }
  }, []);

  useEffect(() => { void cargar(); }, [cargar]);

  async function renombrar(code: string, name: string): Promise<void> {
    if (name.trim() === '') { setError('El nombre no puede quedar vacío.'); return; }
    setError(null); setGuardando(true);
    try {
      const { error: e } = await supabase.from('campaign_types')
        .update({ name: name.trim() }).eq('code', code);
      if (e) throw new Error(e.message);
      setAviso(`"${name.trim()}" actualizado.`);
      await cargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo renombrar.');
    } finally { setGuardando(false); }
  }

  async function alternar(t: CampaignTypeRow): Promise<void> {
    setError(null); setGuardando(true);
    try {
      const { error: e } = await supabase.from('campaign_types')
        .update({ active: !t.active }).eq('code', t.code);
      if (e) throw new Error(e.message);
      setAviso(
        t.active
          ? `"${t.name}" quedó inactivo: ya no se ofrece al crear campañas. Las que ya lo usan no cambian.`
          : `"${t.name}" quedó activo.`,
      );
      await cargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cambiar el estado.');
    } finally { setGuardando(false); }
  }

  async function crear(): Promise<void> {
    setError(null);
    const code = nuevoCodigo.trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_');
    if (code === '') { setError('Ponle un código al tipo (por ejemplo: mystery_shopper).'); return; }
    if (nuevoNombre.trim() === '') { setError('Ponle un nombre, que es lo que se ve en pantalla.'); return; }
    if (todos.some((t) => t.code === code)) { setError(`El código "${code}" ya existe.`); return; }
    setGuardando(true);
    try {
      const { error: e } = await supabase.from('campaign_types').insert({
        code,
        name: nuevoNombre.trim(),
        sort: Math.max(0, ...todos.map((t) => t.sort)) + 10,
      });
      if (e) throw new Error(e.message);
      setAviso(`Tipo "${nuevoNombre.trim()}" creado con el código ${code}.`);
      setNuevoCodigo(''); setNuevoNombre('');
      await cargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear el tipo.');
    } finally { setGuardando(false); }
  }

  const visibles = verInactivos ? todos : tipos;

  return (
    <section className="max-w-2xl">
      <h2 className="mb-1 font-semibold">Tipos de campaña</h2>
      <p className="mb-3 text-sm text-gray-600">
        Es la lista que aparece al crear o editar una campaña. El <strong>código</strong> no se
        puede cambiar después: es la referencia que guardan las campañas.
      </p>

      {error && <p className="mb-2 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">{error}</p>}
      {aviso && <p className="mb-2 rounded border border-blue-200 bg-blue-50 p-2 text-sm text-blue-800">{aviso}</p>}

      <label className="mb-2 flex items-center gap-2 text-sm">
        <input type="checkbox" checked={verInactivos} onChange={(e) => setVerInactivos(e.target.checked)} />
        Ver también los inactivos
      </label>

      <table className="w-full border text-sm">
        <thead className="bg-gray-100 text-left">
          <tr><th className="p-2">Código</th><th className="p-2">Nombre</th><th className="p-2">Estado</th><th className="p-2" /></tr>
        </thead>
        <tbody>
          {visibles.map((t) => (
            <tr key={t.code} className="border-t">
              <td className="p-2 font-mono text-xs text-gray-600">{t.code}</td>
              <td className="p-2">
                <input
                  defaultValue={t.name}
                  onBlur={(e) => { if (e.target.value !== t.name) void renombrar(t.code, e.target.value); }}
                  className="w-full rounded border px-2 py-1"
                />
              </td>
              <td className="p-2">{t.active ? 'Activo' : <span className="text-gray-500">Inactivo</span>}</td>
              <td className="p-2 text-right">
                <button
                  type="button" disabled={guardando}
                  onClick={() => void alternar(t)}
                  className="text-sm text-gray-600 underline disabled:opacity-50"
                >
                  {t.active ? 'Desactivar' : 'Activar'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-4 rounded border bg-gray-50 p-3">
        <h3 className="mb-2 text-sm font-medium">Agregar un tipo</h3>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm">
            Código
            <input
              value={nuevoCodigo}
              onChange={(e) => setNuevoCodigo(e.target.value)}
              placeholder="mystery_shopper"
              className="mt-1 block rounded border px-2 py-1 font-mono text-xs"
            />
          </label>
          <label className="text-sm">
            Nombre
            <input
              value={nuevoNombre}
              onChange={(e) => setNuevoNombre(e.target.value)}
              placeholder="Mystery shopper"
              className="mt-1 block rounded border px-2 py-1"
            />
          </label>
          <button
            type="button" disabled={guardando}
            onClick={() => void crear()}
            className="rounded bg-blue-600 px-3 py-2 text-sm text-white disabled:opacity-50"
          >
            Agregar
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          El código se normaliza solo (minúsculas y guion bajo). Elígelo pensando que no se va a
          poder cambiar.
        </p>
      </div>
    </section>
  );
}
